import { useEffect, useRef, useState } from 'react'
import { Link2, Plus, Search, Trash2, X } from 'lucide-react'
import {
  patchContact,
  listContacts,
  addContactRelation,
  removeContactRelation,
} from '../../api/contacts'
import useTenantVertical from '../../hooks/useTenantVertical'

// Suggested attribute keys per vertical (Phase 4 multi-vertical). Free-form
// keys are always allowed — these only pre-populate the "add field" picker.
const SUGGESTED_ATTRIBUTES = {
  spa: ['skin_type', 'allergies', 'preferred_therapist', 'package_balance'],
  education: ['level', 'class', 'school'],
  hotel: ['room_preference', 'loyalty_tier'],
  fnb: ['dietary_preference', 'favorite_table'],
  retail: [],
  other: [],
}

const CONSENT_KEYS = ['marketing', 'sensitive_data']
const RELATIONS = ['guardian', 'student', 'family', 'other']

const sectionTitleCls =
  'text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2'
const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-[#1535c8]'

export default function ContactVerticalSection({ tenantId, contact, t, onUpdated }) {
  const vertical = useTenantVertical()
  const [attrDraft, setAttrDraft] = useState(contact?.attributes || {})
  const [newKey, setNewKey] = useState('')
  const [savingAttrs, setSavingAttrs] = useState(false)
  const [savingConsent, setSavingConsent] = useState(null)
  const [relationQuery, setRelationQuery] = useState('')
  const [relationResults, setRelationResults] = useState([])
  const [relationChoice, setRelationChoice] = useState('guardian')
  const [showLinkForm, setShowLinkForm] = useState(false)
  const [linking, setLinking] = useState(false)
  const searchTimer = useRef(null)

  useEffect(() => {
    setAttrDraft(contact?.attributes || {})
    setShowLinkForm(false)
    setRelationQuery('')
    setRelationResults([])
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset drafts only when switching contacts
  }, [contact?.id])

  useEffect(() => {
    if (!showLinkForm || !relationQuery.trim()) {
      setRelationResults([])
      return undefined
    }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      listContacts(tenantId, { search: relationQuery.trim(), limit: 5 })
        .then((d) => {
          const items = (d?.items || d?.contacts || [])
            .filter((c) => c.id !== contact?.id)
            .slice(0, 5)
          setRelationResults(items)
        })
        .catch(() => setRelationResults([]))
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [relationQuery, showLinkForm, tenantId, contact?.id])

  if (!contact?.id) return null

  const attrsDirty =
    JSON.stringify(attrDraft) !== JSON.stringify(contact?.attributes || {})
  const consents = contact?.consents || {}
  const relations = contact?.related_contacts || []
  const suggestions = (SUGGESTED_ATTRIBUTES[vertical] || []).filter(
    (k) => !(k in attrDraft),
  )

  function attrLabel(key) {
    return t?.(`contacts.attributeKeys.${key}`, { defaultValue: key.replace(/_/g, ' ') })
  }

  async function saveAttributes() {
    setSavingAttrs(true)
    try {
      const cleaned = {}
      Object.entries(attrDraft).forEach(([k, v]) => {
        const key = String(k).trim()
        if (key && String(v ?? '').trim() !== '') cleaned[key] = String(v).trim()
      })
      const updated = await patchContact(tenantId, contact.id, { attributes: cleaned })
      onUpdated?.(updated)
    } catch {
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setSavingAttrs(false)
    }
  }

  async function toggleConsent(key) {
    setSavingConsent(key)
    try {
      const updated = await patchContact(tenantId, contact.id, {
        consents: { ...consents, [key]: !consents[key] },
      })
      onUpdated?.(updated)
    } catch {
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setSavingConsent(null)
    }
  }

  async function linkContact(target) {
    setLinking(true)
    try {
      const updated = await addContactRelation(tenantId, contact.id, {
        related_contact_id: target.id,
        relation: relationChoice,
      })
      onUpdated?.(updated)
      setShowLinkForm(false)
      setRelationQuery('')
      setRelationResults([])
    } catch {
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setLinking(false)
    }
  }

  async function unlinkContact(relatedId) {
    setLinking(true)
    try {
      const updated = await removeContactRelation(tenantId, contact.id, relatedId)
      onUpdated?.(updated)
    } catch {
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setLinking(false)
    }
  }

  return (
    <>
      {/* Vertical-specific attributes */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className={sectionTitleCls}>
          {t?.('contacts.attributesTitle') || 'Profile details'}
        </div>
        <div className="space-y-2">
          {Object.entries(attrDraft).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-32 shrink-0 truncate capitalize">
                {attrLabel(key)}
              </span>
              <input
                className={inputCls}
                value={value ?? ''}
                onChange={(e) =>
                  setAttrDraft((prev) => ({ ...prev, [key]: e.target.value }))
                }
              />
              <button
                className="text-gray-300 hover:text-red-400"
                onClick={() =>
                  setAttrDraft((prev) => {
                    const next = { ...prev }
                    delete next[key]
                    return next
                  })
                }
                aria-label={t?.('contacts.removeAttribute') || 'Remove field'}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {suggestions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((key) => (
                <button
                  key={key}
                  className="text-xs border border-dashed border-gray-300 text-gray-500 rounded-full px-2.5 py-1 hover:border-[#1535c8] hover:text-[#1535c8] capitalize"
                  onClick={() => setAttrDraft((prev) => ({ ...prev, [key]: '' }))}
                >
                  + {attrLabel(key)}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              className={inputCls}
              placeholder={t?.('contacts.addAttributePlaceholder') || 'Custom field name…'}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newKey.trim()) {
                  const key = newKey.trim().toLowerCase().replace(/\s+/g, '_')
                  setAttrDraft((prev) => ({ [key]: '', ...prev }))
                  setNewKey('')
                }
              }}
            />
            <button
              className="text-gray-400 hover:text-[#1535c8] disabled:opacity-40"
              disabled={!newKey.trim()}
              onClick={() => {
                const key = newKey.trim().toLowerCase().replace(/\s+/g, '_')
                setAttrDraft((prev) => ({ [key]: '', ...prev }))
                setNewKey('')
              }}
              aria-label={t?.('contacts.addAttribute') || 'Add field'}
            >
              <Plus size={16} />
            </button>
          </div>
          {attrsDirty && (
            <button
              className="bg-[#1535c8] text-white text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-50"
              onClick={saveAttributes}
              disabled={savingAttrs}
            >
              {savingAttrs
                ? t?.('contacts.saving') || 'Saving…'
                : t?.('contacts.saveAttributes') || 'Save details'}
            </button>
          )}
        </div>
      </div>

      {/* Consents */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className={sectionTitleCls}>
          {t?.('contacts.consentsTitle') || 'Consents'}
        </div>
        <div className="flex flex-wrap gap-2">
          {CONSENT_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => toggleConsent(key)}
              disabled={savingConsent === key}
              className={`text-xs rounded-full px-3 py-1.5 border font-medium disabled:opacity-50 ${
                consents[key]
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}
            >
              {t?.(`contacts.consentKeys.${key}`) || key.replace(/_/g, ' ')}
              {consents[key] ? ' ✓' : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Related contacts */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <div className={sectionTitleCls} style={{ marginBottom: 0 }}>
            {t?.('contacts.relationsTitle') || 'Related contacts'}
          </div>
          <button
            className="text-xs text-[#1535c8] font-semibold inline-flex items-center gap-1"
            onClick={() => setShowLinkForm((v) => !v)}
          >
            {showLinkForm ? <X size={13} /> : <Link2 size={13} />}
            {showLinkForm
              ? t?.('contacts.cancel') || 'Cancel'
              : t?.('contacts.linkContact') || 'Link'}
          </button>
        </div>
        {relations.length === 0 && !showLinkForm && (
          <p className="text-xs text-gray-400">
            {t?.('contacts.noRelations') || 'No linked contacts yet.'}
          </p>
        )}
        <div className="space-y-1.5">
          {relations.map((rel) => (
            <div
              key={rel.contact_id}
              className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <span className="font-medium text-gray-800 truncate">
                  {rel.name || rel.contact_id}
                </span>
                <span className="ml-2 text-xs text-gray-400">
                  {t?.(`contacts.relationLabels.${rel.relation}`) || rel.relation}
                </span>
              </div>
              <button
                className="text-gray-300 hover:text-red-400 disabled:opacity-40"
                onClick={() => unlinkContact(rel.contact_id)}
                disabled={linking}
                aria-label={t?.('contacts.unlink') || 'Unlink'}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        {showLinkForm && (
          <div className="mt-2 space-y-2">
            <div className="flex gap-2">
              <select
                className={`${inputCls} w-auto`}
                value={relationChoice}
                onChange={(e) => setRelationChoice(e.target.value)}
              >
                {RELATIONS.map((r) => (
                  <option key={r} value={r}>
                    {t?.(`contacts.relationLabels.${r}`) || r}
                  </option>
                ))}
              </select>
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300"
                />
                <input
                  className={`${inputCls} pl-8`}
                  placeholder={t?.('contacts.searchToLink') || 'Search name or phone…'}
                  value={relationQuery}
                  onChange={(e) => setRelationQuery(e.target.value)}
                />
              </div>
            </div>
            {relationResults.map((c) => (
              <button
                key={c.id}
                className="w-full text-left text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-[#1535c8] disabled:opacity-50"
                onClick={() => linkContact(c)}
                disabled={linking}
              >
                <span className="font-medium text-gray-800">{c.name || '—'}</span>
                {c.phone && <span className="ml-2 text-xs text-gray-400">{c.phone}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
